import get from 'lodash/get';
import includes from 'lodash/includes';
import { createSelector } from 'reselect';
import moment from 'moment';
import DataSources from 'modules/DataSources';
import * as constants from './constants';
import * as actionTypes from './actionTypes';
/**
 * Input selectors
 */ const mainSelector = (state)=>state[constants.MODULE_ID];
const fetchingSelector = (state)=>state[constants.MODULE_ID].fetching;
const sendingSelector = (state)=>state[constants.MODULE_ID].sending;
const errorsSelector = (state)=>state[constants.MODULE_ID].errors;
//----------------------------------------------------------------------------------------------------------------------
/**
 * Patients list
 */ export const patients = (state)=>mainSelector(state).patients;
/**
 * Enrolling sharing request / pending patients
 */ export const enrollingSharingRequests = (state)=>mainSelector(state).enrollingSharingRequests;
/**
 * Current clinic patients profiles document id
 */ export const profilesDocumentId = (state)=>mainSelector(state).profilesDocumentId;
/**
 * Patients search
 */ export const searchSelector = (id)=>(state)=>get(mainSelector(state).search, id, '');
/**
 * Clinics search
 */ export const searchClinicsSelector = (id)=>(state)=>get(mainSelector(state).searchClinics, id, '');
/**
 * Active patient
 */ export const activePatient = (state)=>mainSelector(state).activePatient;
/**
 * Active patient PHI set
 */ export const phiSet = (state)=>mainSelector(state).phiSet;
/**
 * Active patient PHI set document id
 */ export const phiSetDocumentId = (state)=>mainSelector(state).phiSetDocumentId;
/**
 * ID of a Patient who own loaded PHI set
 */ export const phiSetPatientId = (state)=>mainSelector(state).phiSetPatientId;
/**
 * Active patient fetched readings monthly batches index
 */ export const batchesIndex = (state)=>mainSelector(state).batchesIndex;
/**
 * Active patient fetched cgm weekly batches index
 */ export const cgmBatchesIndex = (state)=>mainSelector(state).cgmBatchesIndex;
/**
 * Active patient fetched notes monthly batches index
 */ export const notesBatchesIndex = (state)=>mainSelector(state).notesBatchesIndex;
/**
 * Active patient fetched measurements monthly batches index
 */ export const measurementsBatchesIndex = (state)=>mainSelector(state).measurementsBatchesIndex;
/**
 * Active patient blood glucose readings
 */ export const readings = (state)=>mainSelector(state).readings;
/**
 * Active patient cgm readings
 */ export const cgmReadings = (state)=>mainSelector(state).cgmReadings;
/**
 * Active patient blood glucose related data
 */ export const relatedData = (state)=>mainSelector(state).relatedData;
/**
 * Meal, insulin, activity, medicaments 3rd app records
 */ export const timeSeriesResources = (state)=>mainSelector(state).timeSeriesResources;
/**
 * Blood glucose highlightedReadings
 */ export const highlightedReadings = (state)=>mainSelector(state).highlightedReadings;
/**
 * Active patient measurements
 */ export const measurements = (state)=>mainSelector(state).measurements;
/**
 * Active patient blood glucose readings with related data
 */ export const readingsWithRelatedData = createSelector([
    relatedData,
    readings
], (relatedDataState, readingsState)=>readingsState.map((reading)=>{
        const findRelatedData = relatedDataState && relatedDataState.find((item)=>item.readingId === reading.externalId);
        if (findRelatedData) {
            reading.relatedData = findRelatedData;
        }
        return reading;
    }));
/**
 * Active patient blood glucose readings in range Date
 */ export const readingsRangeDate = (daysEndOffset, daysBeforeEndOffset = 30)=>createSelector(readings, (readingsState)=>{
        const lastPossibleMoment = moment.utc().add(-daysEndOffset, 'days').endOf('day');
        const firstPossibleMoment = lastPossibleMoment.clone().add(-(daysBeforeEndOffset - 1), 'days').startOf('day');
        const firstPossibleTimestamp = firstPossibleMoment.unix();
        const lastPossibleTimestamp = lastPossibleMoment.unix();
        return readingsState.filter((reading)=>reading.timestamp > firstPossibleTimestamp && reading.timestamp < lastPossibleTimestamp);
    });
//----------------------------------------------------------------------------------------------------------------------
export const notes = (state)=>mainSelector(state).notes;
//----------------------------------------------------------------------------------------------------------------------
/**
 * Active patient sharing request
 */ export const sharingRequest = (state)=>mainSelector(state).sharingRequest;
/**
 * Received sharing request in qr code add patient process
 */ export const receivedSharingRequest = (state)=>mainSelector(state).receivedSharingRequest;
//----------------------------------------------------------------------------------------------------------------------
/**
 * Selects patient data differences
 */ export const patientDataComparison = (state)=>mainSelector(state).patientDataComparison;
/**
 * Indicates if hide patient data differences according to snapshots
 */ export const isPatientDataFullComparison = (state)=>mainSelector(state).isPatientDataFullComparison;
//----------------------------------------------------------------------------------------------------------------------
/**
 * Default settings for account country
 */ export const countrySettings = (state)=>mainSelector(state).countrySettings;
/**
 * Select custom patient information template dependent on country
 */ export const informationTemplate = (state)=>mainSelector(state).informationTemplate;
/**
 * Select custom patient information template dependent on country
 */ export const clinicPatientTemplate = (state)=>mainSelector(state).clinicPatientTemplate;
/**
 * Select custom patient identifiers dependent on country
 */ export const patientCustomIdentifiers = (state)=>mainSelector(state).patientCustomIdentifiers;
/**
 * Select payers dependent on country
 */ export const payers = (state)=>mainSelector(state).payers;
//----------------------------------------------------------------------------------------------------------------------
export const isFetchPatientsInProgress = createSelector(fetchingSelector, (state)=>includes(state, actionTypes.FETCH_PATIENTS));
export const isFetchEnrollingSharingRequestsInProgress = createSelector(fetchingSelector, (state)=>includes(state, actionTypes.FETCH_ENROLLING_SHARING_REQUESTS));
export const hasFetchEnrollingSharingRequestsErrors = createSelector(errorsSelector, (state)=>includes(state, actionTypes.FETCH_ENROLLING_SHARING_REQUESTS));
//----------------------------------------------------------------------------------------------------------------------
export const isActivatePatientInProgress = createSelector(fetchingSelector, (state)=>includes(state, actionTypes.ACTIVATE_PATIENT));
//----------------------------------------------------------------------------------------------------------------------
export const isAddPatientInProgress = createSelector(sendingSelector, (state)=>includes(state, actionTypes.ADD_PATIENT) || includes(state, actionTypes.FETCH_PATIENTS));
export const hasAddPatientErrors = createSelector(errorsSelector, (state)=>includes(state, actionTypes.ADD_PATIENT));
//----------------------------------------------------------------------------------------------------------------------
export const isEnrollPatientInProgress = createSelector(sendingSelector, (state)=>includes(state, actionTypes.ENROLL_PATIENT));
export const hasEnrollPatientErrors = createSelector(errorsSelector, (state)=>includes(state, actionTypes.ENROLL_PATIENT));
//----------------------------------------------------------------------------------------------------------------------
export const isMergePatientInProgress = createSelector(sendingSelector, (state)=>includes(state, actionTypes.MERGE_PATIENT));
export const hasMergePatientErrors = createSelector(errorsSelector, (state)=>includes(state, actionTypes.MERGE_PATIENT));
//----------------------------------------------------------------------------------------------------------------------
export const isUpdatePatientInProgress = createSelector(sendingSelector, (state)=>includes(state, actionTypes.UPDATE_PATIENT));
export const hasUpdatePatientErrors = createSelector(errorsSelector, (state)=>includes(state, actionTypes.UPDATE_PATIENT));
//----------------------------------------------------------------------------------------------------------------------
export const isStartPatientVisitInProgress = createSelector(sendingSelector, (state)=>includes(state, actionTypes.START_PATIENT_VISIT));
export const hasStartPatientVisitErrors = createSelector(errorsSelector, (state)=>includes(state, actionTypes.START_PATIENT_VISIT));
//----------------------------------------------------------------------------------------------------------------------
export const isEndPatientVisitInProgress = createSelector(sendingSelector, (state)=>includes(state, actionTypes.END_PATIENT_VISIT));
export const hasEndPatientVisitErrors = createSelector(errorsSelector, (state)=>includes(state, actionTypes.END_PATIENT_VISIT));
//----------------------------------------------------------------------------------------------------------------------
export const isRemovePatientInProgress = createSelector(sendingSelector, (state)=>includes(state, actionTypes.REMOVE_PATIENT) || includes(state, actionTypes.FETCH_PATIENTS));
export const hasRemovePatientErrors = createSelector(errorsSelector, (state)=>includes(state, actionTypes.REMOVE_PATIENT));
//----------------------------------------------------------------------------------------------------------------------
export const isReassignHcpInProgress = createSelector(sendingSelector, (state)=>includes(state, actionTypes.REASSIGN_HCP));
export const hasReassignHcpErrors = createSelector(errorsSelector, (state)=>includes(state, actionTypes.REASSIGN_HCP));
//----------------------------------------------------------------------------------------------------------------------
export const isCreateSharingRequestInProgress = createSelector(sendingSelector, (state)=>includes(state, actionTypes.CREATE_SHARING_REQUEST));
export const hasCreateSharingRequestErrors = createSelector(errorsSelector, (state)=>includes(state, actionTypes.CREATE_SHARING_REQUEST));
//----------------------------------------------------------------------------------------------------------------------
export const isRemoveSharingRequestInProgress = createSelector(sendingSelector, (state)=>includes(state, actionTypes.REMOVE_SHARING_REQUEST));
export const hasRemoveSharingRequestErrors = createSelector(errorsSelector, (state)=>includes(state, actionTypes.REMOVE_SHARING_REQUEST));
//----------------------------------------------------------------------------------------------------------------------
export const isResendInvitationPatientInProgress = createSelector(sendingSelector, (state)=>includes(state, actionTypes.RESEND_SHARING_REQUEST));
export const resendInvitationPatientErrors = createSelector(errorsSelector, (state)=>includes(state, actionTypes.RESEND_SHARING_REQUEST));
//----------------------------------------------------------------------------------------------------------------------
export const isBindSharingRequestWithClinicPatientInProgress = createSelector(sendingSelector, (state)=>includes(state, actionTypes.BIND_SHARING_REQUEST_WITH_CLINIC_PATIENT));
export const hasBindSharingRequestWithClinicPatientErrors = createSelector(errorsSelector, (state)=>includes(state, actionTypes.BIND_SHARING_REQUEST_WITH_CLINIC_PATIENT));
//----------------------------------------------------------------------------------------------------------------------
export const isSyncInProgress = createSelector(fetchingSelector, DataSources.selectors.isSyncInProgress, (state, isSyncInProgressState)=>includes(state, actionTypes.SYNC) || isSyncInProgressState);
export const hasSyncErrors = createSelector(errorsSelector, DataSources.selectors.hasSyncErrors, (state, hasSyncErrorsState)=>includes(state, actionTypes.SYNC) || hasSyncErrorsState);
export const isStorePatientInfoInProgress = createSelector(fetchingSelector, (state)=>includes(state, actionTypes.STORE_PATIENT_INFO));
export const hasStorePatientInfoErrors = createSelector(fetchingSelector, (state)=>includes(state, actionTypes.STORE_PATIENT_INFO_ERROR));
//----------------------------------------------------------------------------------------------------------------------
export const isFetchPatientCountryLocalizationInProgress = createSelector(fetchingSelector, (state)=>includes(state, actionTypes.FETCH_PATIENT_COUNTRY_LOCALIZATION));
export const hasFetchPatientCountryLocalizationErrors = createSelector(errorsSelector, (state)=>includes(state, actionTypes.FETCH_PATIENT_COUNTRY_LOCALIZATION));
//----------------------------------------------------------------------------------------------------------------------
export const isAddPatientToFavoritesInProgess = createSelector(fetchingSelector, (state)=>includes(state, actionTypes.ADD_PATIENT_TO_FAVORITE));
